import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { BackNavLink } from '../../components/NavButtons';
import NotificationThreadSection from '../../components/NotificationThreadSection';
import { useAuth } from '../../contexts/AuthContext';
import {
  getEventNotificationLabel,
  getEventNotificationResourceLabel,
  getEventNotificationResourcePath,
  getNotificationById,
  getNotificationSenderTitle,
  isEventNotification,
  isParentNotification,
  markNotificationAsSeen,
} from '../../services/notificationService';

function formatNotificationDate(value) {
  if (!value) {
    return 'بدون تاریخ';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return 'بدون تاریخ';
  }

  return new Intl.DateTimeFormat('fa-IR', {
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

function NotificationDetailsPage() {
  const { notificationId } = useParams();
  const { user } = useAuth();
  const [notification, setNotification] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    let isMounted = true;

    async function loadNotification() {
      setIsLoading(true);
      setErrorMessage('');

      try {
        const notificationData = await getNotificationById(notificationId);

        if (!isMounted) {
          return;
        }

        setNotification(notificationData);

        if (notificationData && !notificationData.isSeen) {
          try {
            const seenNotification = await markNotificationAsSeen(notificationId);

            if (isMounted && seenNotification) {
              setNotification(seenNotification);
            }
          } catch {
            if (isMounted) {
              setNotification(notificationData);
            }
          }
        }
      } catch (error) {
        if (isMounted) {
          setErrorMessage(error.message || 'امکان دریافت جزئیات اطلاعیه وجود ندارد.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadNotification();

    return () => {
      isMounted = false;
    };
  }, [notificationId]);

  const isEvent = notification && isEventNotification(notification);
  const notificationsListPath = notification && isParentNotification(notification)
    ? '/parents'
    : isEvent
      ? '/notifications?tab=alerts'
      : '/notifications';
  const notificationsListLabel = notification && isParentNotification(notification)
    ? 'بازگشت به اطلاعیه‌های والدین'
    : isEvent
      ? 'بازگشت به اطلاعیه‌ها'
      : 'بازگشت به پیام‌ها';
  const eventLabel = isEvent ? getEventNotificationLabel(notification) : '';
  const resourcePath = isEvent ? getEventNotificationResourcePath(notification) : null;
  const resourceLabel = isEvent ? getEventNotificationResourceLabel(notification) : '';

  return (
    <div className="space-y-5">
      <BackNavLink to={notificationsListPath}>{notificationsListLabel}</BackNavLink>

      {isLoading ? (
        <div className="rounded-[12px] border border-slate-100 bg-white px-4 py-10 text-center text-[12px] text-slate-500">
          در حال دریافت جزئیات اطلاعیه...
        </div>
      ) : errorMessage ? (
        <div className="rounded-[12px] border border-slate-100 bg-white px-4 py-6 text-center">
          <p className="text-[13px] text-[#E02727]">{errorMessage}</p>
          <Link
            to={notificationsListPath}
            className="mt-3 inline-flex rounded-[8px] bg-[#00C0B9] px-5 py-2 text-[12px] font-medium text-white"
          >
            بازگشت
          </Link>
        </div>
      ) : !notification ? (
        <div className="rounded-[12px] border border-slate-100 bg-white px-4 py-8 text-center">
          <p className="text-[15px] font-medium text-slate-800">اطلاعیه پیدا نشد</p>
          <p className="mt-2 text-[12px] text-slate-500">این اطلاعیه در دسترس نیست.</p>
        </div>
      ) : (
        <article className="overflow-hidden rounded-[12px] border border-slate-100 bg-white">
          <header className="border-b border-slate-100 px-4 py-4 sm:px-5">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-slate-400">
              <span>
                {getNotificationSenderTitle(notification, { currentUserId: user?.id })}
              </span>
              <span aria-hidden="true">·</span>
              <time dateTime={notification.createdAt}>
                {formatNotificationDate(notification.createdAt)}
              </time>
              <span aria-hidden="true">·</span>
              <span>{notification.isSeen ? 'خوانده‌شده' : 'جدید'}</span>
              {eventLabel ? (
                <>
                  <span aria-hidden="true">·</span>
                  <span>{eventLabel}</span>
                </>
              ) : null}
            </div>

            <h1 className="mt-3 text-[16px] font-medium leading-7 text-slate-800">
              {notification.title || 'بدون عنوان'}
            </h1>
          </header>

          <div className="space-y-5 px-4 py-5 sm:px-5">
            {isEvent ? (
              <div className="space-y-4">
                <p className="whitespace-pre-line text-[14px] leading-7 text-slate-700">
                  {notification.description || 'متنی برای این اطلاعیه ثبت نشده است.'}
                </p>
                {resourcePath && resourceLabel ? (
                  <Link
                    to={resourcePath}
                    className="inline-flex rounded-[8px] bg-teal-50 px-4 py-2 text-[12px] font-medium text-teal-700"
                  >
                    {resourceLabel}
                  </Link>
                ) : null}
              </div>
            ) : (
              <NotificationThreadSection notification={notification} currentUserId={user?.id} />
            )}
          </div>

          <footer className="border-t border-slate-100 px-4 py-4 sm:px-5">
            {notification.senderId !== user?.id ? (
              <p className="text-[11px] leading-5 text-slate-400">
                با باز کردن این صفحه، اطلاعیه به‌صورت خودکار خوانده‌شده ثبت می‌شود.
              </p>
            ) : null}

            <Link
              to={notificationsListPath}
              className={`flex w-full items-center justify-center rounded-[8px] bg-[#00C0B9] py-2.5 text-[13px] font-medium text-white transition active:scale-[0.99] ${
                notification.senderId !== user?.id ? 'mt-3' : ''
              }`}
            >
              {notificationsListLabel}
            </Link>
          </footer>
        </article>
      )}
    </div>
  );
}

export default NotificationDetailsPage;
